import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, makeStyles } from "@material-ui/core";
import { useLoanStore } from '@roc/feature-loans';
import { ConfirmDialog, Paper, PaperBorderType, SelectField } from '@roc/ui';

const useStyles = makeStyles(() => ({
  gridContainer: {
    padding: '0 16px 16px 16px',
  },
  selectFieldContainer: {
    '& .MuiFormControl-root.MuiFormControl-fullWidth': {
      padding: '0 5px 0 10px',
      borderRadius: '5px',
      border: '1px solid #E4E4E7 !important',
      '& .MuiInput-underline': {
        '&:before': {
          transition: 'none !important',
          borderBottom: 'none !important',
        },
        '&:after': {
          transform: 'none !important',
          transition: 'none !important',
          borderBottom: 'none !important',
        },
      },
    },
  },
}));

interface SpecialistOption {
  label: string;
  value: string;
}

interface Loan {
  loanId: number;
  payoffSpecialistFullName?: string;
}

interface SpecialistsDropdown {
  payoffSpecialists?: SpecialistOption[];
}

interface LoanActorsProps {
  loan?: Loan;
  specialistsDropdown?: SpecialistsDropdown;
}

export default function PayoffLoanActors({ loan, specialistsDropdown }: LoanActorsProps) {
  const classes = useStyles();
  const { loanStore } = useLoanStore();

  const [selectedPayoffSpecialist, setSelectedPayoffSpecialist] = useState<SpecialistOption | undefined | null>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const payoffSpecialistOptions = useMemo(() => specialistsDropdown?.payoffSpecialists, [specialistsDropdown]);

  const handleConfirmRef = useRef<() => Promise<void>>();

  const updateSpecialists = useCallback((payoffSpecialistOption?: SpecialistOption) => {
    return loanStore.updateSpecialists(loan?.loanId,
      {
        payoffSpecialistId: payoffSpecialistOption?.value ? +payoffSpecialistOption?.value : undefined,
      });
  }, [loanStore, loan]);

  const confirmUpdateSpecialists = useCallback(async (payoffSpecialist?: SpecialistOption) => {
    updateSpecialists(payoffSpecialist);

    setSelectedPayoffSpecialist((prevPayoffSpecialist) => payoffSpecialist || prevPayoffSpecialist);

    setIsConfirmOpen(false);
    setDialogMessage('');
  }, [updateSpecialists]);

  const openDialog = useCallback((specialistType: string, currentSpecialistName: string, newSpecialistName: string, payoffSpecialist?: SpecialistOption) => {
    setDialogMessage(`Are you sure you want to change the ${specialistType} Specialist from ${currentSpecialistName} to ${newSpecialistName}?`);
    setIsConfirmOpen(true);

    return () => {
      return confirmUpdateSpecialists(payoffSpecialist);
    };
  }, [confirmUpdateSpecialists]);

  const findSpecialistOption = (specialistValue: string, prop: keyof SpecialistOption, specialistOptions: SpecialistOption[]) => {
    return specialistOptions.find(specialist => specialist[prop] === specialistValue);
  };

  const handlePayoffSpecialistChange = useCallback((optionValue?: string) => {
    const specialist = findSpecialistOption(optionValue, 'value', payoffSpecialistOptions);

    if (specialist && specialist !== selectedPayoffSpecialist) {
      handleConfirmRef.current = openDialog('Payoff', selectedPayoffSpecialist?.label ?? 'none', specialist.label, specialist);
    }
  }, [selectedPayoffSpecialist, payoffSpecialistOptions, openDialog]);

  useEffect(() => {
    if (loan && payoffSpecialistOptions && !selectedPayoffSpecialist) {
      const payoffSpecialist = findSpecialistOption(loan.payoffSpecialistFullName, 'label', payoffSpecialistOptions);

      if (payoffSpecialist) {
        setSelectedPayoffSpecialist(payoffSpecialist);
      }
    }
  }, [loan, selectedPayoffSpecialist, payoffSpecialistOptions]);

  return (
    <>
      <Paper title="Loan Actors" borderType={PaperBorderType.SOLID_BORDER}>
        <Grid
          container
          justifyContent='space-between'
          spacing={2}
          className={classes.gridContainer}
        >
          <Grid item xs={12} md={12} className={classes.selectFieldContainer}>
            {payoffSpecialistOptions &&
              <SelectField
                standaloneLabel
                label='Payoff Specialist'
                value={selectedPayoffSpecialist?.value ?? ''}
                options={payoffSpecialistOptions}
                onChange={handlePayoffSpecialistChange}
                fullWidth
                testId='admin-specialist-select'
              />
            }
          </Grid>
        </Grid>
      </Paper>
      <ConfirmDialog
        open={isConfirmOpen}
        title={'Update Specialist'}
        body={dialogMessage}
        handleCancel={() => setIsConfirmOpen(false)}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={handleConfirmRef.current}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
    </>
  );
}
